// ============================================================================
// PRE-LITIGATION ENDPOINTS (Phase 7)
// ============================================================================
// Backend: PreLitigationController → /api/v1/PreLitigation → BASE_PATH = '/PreLitigation'.

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

export const PRELIT_BASE_PATH = '/PreLitigation';

export const preLitigationEndpoints = {
  // ---------- Pool ----------
  /** GET /api/v1/PreLitigation/pool */
  pool: () => buildEndpoint(`${PRELIT_BASE_PATH}/pool`),

  /** POST /api/v1/PreLitigation/pool/recompute */
  recomputePool: () => buildEndpoint(`${PRELIT_BASE_PATH}/pool/recompute`),

  // ---------- Cases ----------
  /** GET /api/v1/PreLitigation/cases */
  cases: () => buildEndpoint(`${PRELIT_BASE_PATH}/cases`),

  /** GET /api/v1/PreLitigation/cases/:id */
  getById: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id`, { id }),

  /** GET /api/v1/PreLitigation/cases/:id/actions */
  caseActions: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/actions`, { id }),

  /** GET /api/v1/PreLitigation/cases/:id/commitments */
  caseCommitments: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/commitments`, { id }),

  // ---------- Workflow trigger endpoints ----------
  /** POST /api/v1/PreLitigation/cases/:id/claim */
  claim: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/claim`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/assign */
  assign: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/assign`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/actions */
  logAction: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/actions`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/commitments */
  captureCommitment: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/commitments`, { id }),

  /** PUT /api/v1/PreLitigation/commitments/:id/resolve */
  resolveCommitment: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/commitments/:id/resolve`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/escalate */
  escalate: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/escalate`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/close-unclaimed */
  manualClose: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/close-unclaimed`, { id }),

  /** POST /api/v1/PreLitigation/cases/:id/close — Win/Lost close from any active state */
  closeCase: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/close`, { id }),

  /** GET /api/v1/PreLitigation/cases/:id/closure-reasons — PRE_LITIGATION-scoped reasons */
  closureReasons: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/closure-reasons`, { id }),

  // ----- Closed Cases Review worksheet (Resources/info 2.txt §2) -----
  /** GET /api/v1/PreLitigation/closed-cases */
  closedCases: () => buildEndpoint(`${PRELIT_BASE_PATH}/closed-cases`),
  /** POST /api/v1/PreLitigation/cases/:id/confirm-closure-review */
  confirmClosureReview: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/confirm-closure-review`, { id }),
  /** POST /api/v1/PreLitigation/cases/:id/reopen */
  reopen: (id: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/cases/:id/reopen`, { id }),

  // ----- Receipts worksheet (Resources/info 2.txt §3) -----
  /** GET /api/v1/PreLitigation/receipts */
  receipts: () => buildEndpoint(`${PRELIT_BASE_PATH}/receipts`),
  /** POST /api/v1/PreLitigation/receipts/:commitmentId/verify */
  verifyReceipt: (commitmentId: ID) =>
    buildEndpointWithParams(`${PRELIT_BASE_PATH}/receipts/:commitmentId/verify`, { commitmentId }),

  /** GET /api/v1/PreLitigation/agents-by-footprint?branchId=…&roleCode=… */
  agentsByFootprint: (branchId: ID, roleCode = 'recsuit_field_agent') =>
    `${buildEndpoint(`${PRELIT_BASE_PATH}/agents-by-footprint`)}?branchId=${encodeURIComponent(String(branchId))}&roleCode=${encodeURIComponent(roleCode)}`,
} as const;

export default preLitigationEndpoints;
